import { Ctx } from '@milkdown/ctx';
import { editorViewCtx, parserCtx } from '@milkdown/core';
import { Slice } from '@milkdown/kit/prose/model';

import { grammarCheckDecoKey } from './decoration';

export interface GrammarCheckConfig {
  onGrammarCheck: (text: string) => Promise<string>;
}

export function createGrammarCheckHandler(config: GrammarCheckConfig) {
  return async (ctx: Ctx) => {
    const view = ctx.get(editorViewCtx);
    const { state } = view;
    const { selection } = state;

    // Check if text is selected
    if (selection.empty) {
      return false;
    }

    // Extract selected text with newlines between blocks for proper context
    const { from, to } = selection;
    const textContent = state.doc.textBetween(from, to, '\n\n');

    // Normalize text for API call
    const trimmedText = textContent.trim();

    if (!trimmedText) {
      return false;
    }

    // Show loading decoration (if plugin is installed)
    view.dispatch(state.tr.setMeta(grammarCheckDecoKey, { from, to }));

    // Helper to clear decoration
    const clearDecoration = () => {
      view.dispatch(view.state.tr.setMeta(grammarCheckDecoKey, { clear: true }));
    };

    // Call parent-provided callback
    // Parent is responsible for: loading UI, API call, error handling, toasts
    let suggestion: string;
    try {
      suggestion = await config.onGrammarCheck(trimmedText);
    } catch (e) {
      clearDecoration();
      throw e;
    }

    // Handle "OK" response (no changes needed)
    if (suggestion === 'OK') {
      clearDecoration();

      return true;
    }

    // Validate suggestion is non-empty
    if (!suggestion) {
      clearDecoration();

      return false;
    }

    // Re-fetch current state after async operation
    const currentState = view.state;

    // Validate positions are still within document bounds
    if (from >= currentState.doc.content.size || to > currentState.doc.content.size) {
      clearDecoration();

      return false;
    }

    // Verify the original text is still at the same position
    const currentText = currentState.doc.textBetween(from, to, '\n\n');
    if (currentText !== textContent) {
      clearDecoration();

      return false;
    }

    // Parse suggestion as markdown to preserve structure (paragraphs, formatting)
    const parser = ctx.get(parserCtx);
    const parsedDoc = parser(suggestion);

    if (!parsedDoc) {
      clearDecoration();

      return false;
    }

    // Replace selection with parsed content (this also clears the decoration)
    try {
      const tr = currentState.tr.replaceRange(from, to, new Slice(parsedDoc.content, 0, 0)).setMeta(grammarCheckDecoKey, { clear: true });

      view.dispatch(tr.scrollIntoView());
    } catch (e) {
      clearDecoration();
      throw e;
    }

    return true;
  };
}
